import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { Button } from '@/components/ui/button';
import { X, Check } from 'lucide-react';

const ImageCropModal = ({ image, onClose, onCrop }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [saveMode, setSaveMode] = useState('crop-only'); // 'crop-only' or 'save-full'

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const createImage = (url) =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener('load', () => resolve(image));
      image.addEventListener('error', (error) => reject(error));
      image.src = url;
    });

  const getCroppedImg = async (imageSrc, pixelCrop) => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    );

    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          console.error('canvas.toBlob returned null');
          reject(new Error('Failed to create blob from canvas'));
          return;
        }
        const reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onloadend = () => {
          resolve(reader.result);
        };
        reader.onerror = (err) => {
          console.error('FileReader error:', err);
          reject(err);
        };
      }, 'image/jpeg', 0.95);
    });
  };

  const handleSave = async () => {
    try {
      const croppedImage = await getCroppedImg(image, croppedAreaPixels);
      onCrop(croppedImage, saveMode === 'save-full' ? image : null, saveMode);
      onClose();
    } catch (e) {
      console.error('Error cropping image:', e);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 9999,
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '1rem',
          backgroundColor: '#fff',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #e5e7eb',
        }}
      >
        <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>
          Выберите область для аватарки
        </h2>
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '0.5rem',
          }}
        >
          <X size={24} />
        </button>
      </div>

      {/* Cropper Area */}
      <div style={{ position: 'relative', flex: 1, backgroundColor: '#000' }}>
        <Cropper
          image={image}
          crop={crop}
          zoom={zoom}
          aspect={1}
          onCropChange={setCrop}
          onCropComplete={onCropComplete}
          onZoomChange={setZoom}
          cropShape="round"
          showGrid={false}
        />
      </div>

      {/* Controls */}
      <div
        style={{
          padding: '1.5rem',
          backgroundColor: '#fff',
          borderTop: '1px solid #e5e7eb',
        }}
      >
        {/* Zoom Slider */}
        <div style={{ marginBottom: '1.5rem' }}>
          <label
            style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontSize: '0.875rem',
              fontWeight: 500,
            }}
          >
            Масштаб
          </label>
          <input
            type="range"
            value={zoom}
            min={1}
            max={3}
            step={0.1}
            onChange={(e) => setZoom(e.target.value)}
            style={{
              width: '100%',
              height: '8px',
              borderRadius: '4px',
              background: '#e5e7eb',
              outline: 'none',
              opacity: 0.7,
              transition: 'opacity .2s',
            }}
          />
        </div>

        {/* Save Mode Selection */}
        <div style={{ marginBottom: '1.5rem' }}>
          <label
            style={{
              display: 'block',
              marginBottom: '0.75rem',
              fontSize: '0.875rem',
              fontWeight: 500,
            }}
          >
            Режим сохранения
          </label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <label
              style={{
                display: 'flex',
                alignItems: 'start',
                padding: '1rem',
                border: '2px solid',
                borderColor: saveMode === 'crop-only' ? '#667eea' : '#e5e7eb',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                backgroundColor: saveMode === 'crop-only' ? '#f5f7ff' : '#fff',
              }}
            >
              <input
                type="radio"
                name="saveMode"
                value="crop-only"
                checked={saveMode === 'crop-only'}
                onChange={(e) => setSaveMode(e.target.value)}
                style={{ marginRight: '0.75rem', marginTop: '0.25rem' }}
              />
              <div>
                <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>
                  Сохранить только выделенную область
                </div>
                <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                  Будет сохранена только выбранная область как аватарка
                </div>
              </div>
            </label>

            <label
              style={{
                display: 'flex',
                alignItems: 'start',
                padding: '1rem',
                border: '2px solid',
                borderColor: saveMode === 'save-full' ? '#667eea' : '#e5e7eb',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                backgroundColor: saveMode === 'save-full' ? '#f5f7ff' : '#fff',
              }}
            >
              <input
                type="radio"
                name="saveMode"
                value="save-full"
                checked={saveMode === 'save-full'}
                onChange={(e) => setSaveMode(e.target.value)}
                style={{ marginRight: '0.75rem', marginTop: '0.25rem' }}
              />
              <div>
                <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>
                  Сохранить выделенную область и всю картинку
                </div>
                <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                  Клик на аватарку откроет полное изображение
                </div>
              </div>
            </label>
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '0.75rem 1.5rem',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              background: 'white',
              cursor: 'pointer',
              fontSize: '0.95rem',
              fontWeight: 500
            }}
          >
            Отмена
          </button>
          <button
            type="button"
            onClick={handleSave}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#667eea',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '0.95rem',
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <Check size={18} />
            Сохранить
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImageCropModal;
